// God → Gateway: Auto Pay and payouts today, reconciliation against ProtonPay,
// what needs attention, and the shared account's switches.
import { useCallback, useEffect, useState } from "react";
import {
  Activity, AlertTriangle, BookOpen, Clock, KeyRound, Landmark, Radio, Scale, Send, Webhook,
} from "lucide-react";
import { god } from "../../godApi";
import { usePolling } from "../../../components/sc/ui";
import {
  Button, Callout, Empty, Grid, Health, Kpi, Page, Pill, Rows, Section, Skeleton, StatusPill, Table, inr, inrCompact, useNav,
} from "../../kit";

const sumBy = (x, keys) => keys.reduce((a, k) => ({ c: a.c + (x?.[k]?.count || 0), v: a.v + (x?.[k]?.amount || 0) }), { c: 0, v: 0 });
const ORDER = ["creating", "initiated", "pending", "review", "success", "failed", "dropped", "dispute", "error"];

export default function GwOverview() {
  const { go } = useNav();
  const [o, setO] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      setO(await god.gwOverview());
      setErr("");
    } catch (e) {
      setErr(e.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  usePolling(load, 10000);

  if (!o) return <Page>{err ? <Callout tone="red" title="Couldn't load the gateway">{err}</Callout> : <Section><Skeleton rows={6} /></Section>}</Page>;

  const s = o.settings || {};
  const pinT = sumBy(o.payins_today, ["success"]);
  const poT = sumBy(o.payouts_today, ["success"]);
  const open = sumBy(o.payins, ["initiated", "pending"]).c;
  const gap = o.upstream_payout_balance != null ? o.upstream_payout_balance - o.merchant_balances_total : null;

  const attention = [];
  if (!s.configured) attention.push({ tone: "blue", title: "ProtonPay isn't configured", body: "Payins and payouts stay off until the account credentials are saved.", to: "gateway/account", cta: "Configure" });
  if (o.payouts_in_review > 0) attention.push({ tone: "red", title: `${o.payouts_in_review} payout(s) need review`, body: "The bank network didn't answer when they were sent. Confirm with ProtonPay, then resolve each one.", to: "gateway/payouts", cta: "Open payouts" });
  if (o.deliveries_failing > 0) attention.push({ tone: "amber", title: `${o.deliveries_failing} merchant callback(s) failing`, body: "Merchant servers aren't acknowledging payin/payout callbacks. They're retried automatically.", to: "gateway/callbacks/merchants", cta: "Open callbacks" });

  // One row per status seen in either collection (all time).
  const statuses = ORDER.filter((k) => o.payins?.[k] || o.payouts?.[k])
    .concat(Object.keys({ ...o.payins, ...o.payouts }).filter((k) => !ORDER.includes(k)));
  const byStatus = statuses.map((k) => ({ id: k, pin: o.payins?.[k], pout: o.payouts?.[k] }));

  return (
    <Page>
      {err && <Callout tone="amber" title="Showing the last good data">{err}</Callout>}

      <Grid cols={4}>
        <Kpi hero icon={Activity} label="Payins today" value={inr(pinT.v)} sub={`${pinT.c.toLocaleString("en-IN")} successful`} onClick={() => go("gateway/payins")} />
        <Kpi icon={Send} label="Payouts today" value={inrCompact(poT.v)} sub={`${poT.c.toLocaleString("en-IN")} successful`} onClick={() => go("gateway/payouts")} />
        <Kpi icon={BookOpen} label="Owed to merchants" value={inrCompact(o.merchant_balances_total)} sub="sum of all balances" onClick={() => go("gateway/ledger")} />
        <Kpi icon={Landmark} label="ProtonPay payout balance"
          value={o.upstream_payout_balance != null ? inrCompact(o.upstream_payout_balance) : "—"}
          sub={o.upstream_error || (gap == null ? "not connected" : `${gap >= 0 ? "Covered" : "Short"} by ${inr(Math.abs(gap))}`)}
          onClick={() => go("gateway/account")} />
      </Grid>

      {attention.length > 0 && (
        <Section title="Needs attention" icon={AlertTriangle}>
          <div className="gwo-stack">
            {attention.map((a) => (
              <Callout key={a.title} tone={a.tone} title={a.title} action={<Button size="sm" onClick={() => go(a.to)}>{a.cta}</Button>}>
                {a.body}
              </Callout>
            ))}
          </div>
        </Section>
      )}

      <Grid cols={4}>
        <Kpi icon={Clock} label="Open payins" value={open.toLocaleString("en-IN")} sub="auto-checked every 30s" onClick={() => go("gateway/payins")} />
        <Kpi icon={Radio} label="Bank callbacks (1h)" value={o.callbacks_last_hour.toLocaleString("en-IN")}
          sub={o.unverified_callbacks_24h ? `${o.unverified_callbacks_24h} unverified in 24h` : "none unverified in 24h"} onClick={() => go("gateway/callbacks")} />
        <Kpi icon={Webhook} label="Merchant callbacks failing" value={o.deliveries_failing.toLocaleString("en-IN")} sub="retrying or failed" onClick={() => go("gateway/callbacks/merchants")} />
        <Kpi icon={Scale} label="Payouts in review" value={o.payouts_in_review.toLocaleString("en-IN")} sub={o.payouts_in_review ? "resolve before money moves" : "nothing held"} onClick={() => go("gateway/payouts")} />
      </Grid>

      <div className="k-split">
        <Section title="By status" sub="All merchants, all time" icon={Activity} pad={false}>
          <Table rows={byStatus} empty={<Empty title="No gateway transactions yet">Payins and payouts appear here once merchants start using the gateway.</Empty>}
            columns={[
              { key: "status", label: "Status", render: (r) => <StatusPill status={r.id} /> },
              { key: "pin", label: "Payins", align: "right", render: (r) => (r.pin ? `${r.pin.count.toLocaleString("en-IN")} · ${inrCompact(r.pin.amount)}` : <span className="k-faint">—</span>) },
              { key: "pout", label: "Payouts", align: "right", render: (r) => (r.pout ? `${r.pout.count.toLocaleString("en-IN")} · ${inrCompact(r.pout.amount)}` : <span className="k-faint">—</span>) },
            ]} />
        </Section>

        <Section title="ProtonPay account" sub="Shared by every merchant" icon={KeyRound}
          actions={<Button size="sm" onClick={() => go("gateway/account")}>Settings</Button>}>
          <Rows items={[
            ["Credentials", <Health ok={!!s.configured} label={s.configured ? "Configured" : "Not set"} />],
            ["Environment", <Pill tone={s.environment === "live" ? "green" : "amber"}>{(s.environment || "test").toUpperCase()}</Pill>],
            ["Payins (platform)", <Health ok={!!s.payin_enabled} label={s.payin_enabled ? "On" : "Paused"} />],
            ["Payouts (platform)", <Health ok={!!s.payout_enabled} label={s.payout_enabled ? "On" : "Paused"} />],
            ["Merchant code", s.merchant_code, { mono: true }],
            ["Payout balance", o.upstream_payout_balance != null ? inr(o.upstream_payout_balance) : (o.upstream_error || "—")],
            ["Reconciliation", gap == null ? "—" : <Pill tone={gap >= 0 ? "green" : "red"} dot>{gap >= 0 ? "Covered" : "Short"} {inr(Math.abs(gap))}</Pill>],
          ]} />
        </Section>
      </div>

      <style>{`
        .gwo-stack { display: flex; flex-direction: column; gap: 10px; }
      `}</style>
    </Page>
  );
}
